// Listing detail data for each listing with bilingual support

export interface ListingDetailData {
  id: string;
  slug: string;
  en: ListingContent;
  fr: ListingContent;
}

export interface DocumentItem {
  name: string;
  type: string;
  url?: string; // AJOUTÉ : lien réel du document soumis (deals réels uniquement — les mocks n'en ont pas besoin)
  isPremium?: boolean;
  price?: number;
  includedInPlans?: ('essential' | 'essential-plus')[];
  requiresNda?: boolean;
}

export interface ListingContent {
  title: string;
  location: string;
  badges: { label: string; type: 'gold' | 'green' | 'blue' }[];
  mainImage: string;
  thumbnails: string[];
  financials: {
    askingPrice: string;
    cashFlow: string;
    grossRevenue: string;
    sde: string;
    ebitda: string;
    realEstate: string;
    established: string;
    ffe: string;
    inventory: string;
  };
  description: string[];
  detailedInfo: { label: string; value: string }[];
  financingOptions: {
    title: string;
    details: string;
    lenderType?: string;
    loanProductType?: string;
    loanAmountOffered?: number;
    loanTermLengthMonths?: number;
    interestRateType?: string;
    interestRateValue?: number;
    interestRateMarginBps?: number | null;
    interestRateFloor?: number | null;
    amortizationType?: string;
    prepaymentPenalty?: string;
    arrangementFeePercent?: number | null;
    drawFeePercentABL?: number | null;
    exitFeePercent?: number | null;
    feesList?: { description: string; amount: number; type: string }[];
    maintenanceCovenantsRequired?: boolean;
    netDebtToEBITDAMaxAllowed?: number | null;
    dscrMinimumRequired?: number;
    dscrCalculationBase?: string;
    dividendRestrictions?: string;
    collateralRequired?: boolean;
    collateralRequirements?: string[];
    personalGuaranteeRequired?: boolean;
    realEstateConditioned?: boolean;
    isABLOffer?: boolean;
    ABL_AR_EligiblityPercent?: number | null;
    ABL_Inventory_EligiblityPercent?: number | null;
    ABL_Cash_Sweep?: boolean;
    isSubordinatedDebt?: boolean;
    subordinationToSeniorDebt?: boolean;
    interestGracePeriodMonths?: number | null;
    balloonPaymentYear?: number | null;
    balloonPaymentAmount?: number | null;
    creditScoreRequired?: boolean;
    creditScoreMinimum?: number | null;
    acceptForeignCreditScores?: boolean;
    foreignCreditScoreMinimum?: number | null;
    acceptedForeignCreditCountries?: string[];
    acceptForeignCollateral?: boolean;
    foreignCollateralRequirements?: string[];
    estimatedClosingTimelineDays?: number;
    offerExpirationDate?: string;
  }[];
  documents: DocumentItem[];
  isOffMarket?: boolean; // If true, deal is sold directly by owner (not through broker)
  broker?: {
    name: string;
    company: string;
    phone: string;
    email: string;
    image: string;
  };
  seller?: {
    name: string;
    company?: string;
    phone: string;
    email: string;
    image: string;
  };
}

export const LISTING_DETAILS: ListingDetailData[] = [
  // listing-001: Restaurant Franchise
  {
    id: 'listing-001',
    slug: 'restaurant-franchise',
    en: {
      title: 'Established Fast Food Franchise',
      location: 'Montréal, QC, Canada',
      badges: [
        { label: 'Seller Financing', type: 'green' },
        { label: '🇨🇦 Canada', type: 'gold' },
      ],
      mainImage: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1200&h=600&fit=crop',
      thumbnails: [
        'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=200&h=150&fit=crop',
        'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=200&h=150&fit=crop',
        'https://images.unsplash.com/photo-1559329007-40df8a9345d8?w=200&h=150&fit=crop',
        'https://images.unsplash.com/photo-1571997478779-2adcbbe9ab2f?w=200&h=150&fit=crop',
      ],
      financials: {
        askingPrice: '$850,000',
        cashFlow: '$185,000',
        grossRevenue: '$1,200,000',
        sde: '$210,000',
        ebitda: '$195,000',
        realEstate: 'Leased',
        established: '2008',
        ffe: '$120,000',
        inventory: 'Included in price',
      },
      description: [
        'Well-established franchise in a premium location with high traffic in the heart of Montréal. This exceptional opportunity offers over 15 years of continuous operation with a stable team of 18 dedicated employees.',
        'The business benefits from an excellent local reputation and a proven operational system. The restaurant is located in a busy commercial area with ample parking available. Revenue is diversified between dine-in, take-out, and delivery services through multiple popular platforms.',
        'This franchise offers significant growth opportunity with the expansion of delivery services and the possibility of extending operating hours. The current owner will provide comprehensive training and support during the transition.',
      ],
      detailedInfo: [
        { label: 'Location', value: 'Montréal, QC' },
        { label: 'Inventory', value: 'Included in price' },
        { label: 'Real Estate', value: 'Leased' },
        { label: 'Square Footage', value: '2,500 sq ft' },
        { label: 'Lease Duration', value: '5 years remaining' },
        { label: 'Year Established', value: '2008' },
        { label: 'Equipment (FF&E)', value: '$120,000' },
        { label: 'Facilities', value: 'Full kitchen' },
        { label: 'Growth & Expansion', value: 'Strong potential' },
        { label: 'Support & Training', value: 'Full training included' },
        { label: 'Reason for Sale', value: 'Retirement' },
        { label: 'Number of Employees', value: '18' },
      ],
      financingOptions: [
        {
          title: 'Seller Financing',
          details: 'Up to 30% of the sale price',
          lenderType: 'Private Lender',
          loanProductType: 'Seller Note',
          loanAmountOffered: 225000,
          loanTermLengthMonths: 60,
          interestRateType: 'fixed',
          interestRateValue: 6.5,
          amortizationType: 'linear',
          prepaymentPenalty: 'None after 12 months',
          maintenanceCovenantsRequired: false,
          dscrMinimumRequired: 1.25,
          dscrCalculationBase: 'sde',
          collateralRequired: true,
          collateralRequirements: ['All Business Assets (First Lien)'],
          personalGuaranteeRequired: true,
          realEstateConditioned: false,
          creditScoreRequired: true,
          creditScoreMinimum: 680,
          acceptForeignCreditScores: false,
          estimatedClosingTimelineDays: 30,
          offerExpirationDate: '2026-06-30',
        },
        {
          title: 'SBA Loan Pre-Approved',
          details: 'Eligible for SBA 7(a) financing',
          lenderType: 'Commercial Bank',
          loanProductType: 'SBA 7(a)',
          loanAmountOffered: 600000,
          loanTermLengthMonths: 120,
          interestRateType: 'floating',
          interestRateValue: 7.25,
          interestRateMarginBps: 275,
          interestRateFloor: 5.0,
          amortizationType: 'linear',
          prepaymentPenalty: '5% year 1, 3% year 2, 1% year 3',
          arrangementFeePercent: 2.0,
          exitFeePercent: null,
          maintenanceCovenantsRequired: true,
          netDebtToEBITDAMaxAllowed: 4.0,
          dscrMinimumRequired: 1.15,
          dscrCalculationBase: 'ebitda',
          dividendRestrictions: 'Max 50% of net income',
          collateralRequired: true,
          collateralRequirements: ['All Business Assets (First Lien)', 'Equipment & Machinery', 'Personal Guarantee (Secured)'],
          personalGuaranteeRequired: true,
          realEstateConditioned: false,
          creditScoreRequired: true,
          creditScoreMinimum: 680,
          acceptForeignCreditScores: true,
          foreignCreditScoreMinimum: 700,
          acceptedForeignCreditCountries: ['Canada', 'United Kingdom'],
          acceptForeignCollateral: false,
          estimatedClosingTimelineDays: 60,
          offerExpirationDate: '2026-07-15',
        },
        {
          title: 'Conventional Financing',
          details: 'Financing partners available',
          lenderType: 'Commercial Bank',
          loanProductType: 'Term Loan',
          loanAmountOffered: 500000,
          loanTermLengthMonths: 84,
          interestRateType: 'fixed',
          interestRateValue: 8.0,
          amortizationType: 'linear',
          arrangementFeePercent: 1.5,
          exitFeePercent: 0.5,
          maintenanceCovenantsRequired: true,
          dscrMinimumRequired: 1.3,
          dscrCalculationBase: 'cfads',
          collateralRequired: true,
          collateralRequirements: ['All Business Assets (First Lien)', 'Accounts Receivable'],
          personalGuaranteeRequired: true,
          realEstateConditioned: false,
          creditScoreRequired: true,
          creditScoreMinimum: 700,
          acceptForeignCreditScores: false,
          estimatedClosingTimelineDays: 45,
          offerExpirationDate: '2026-06-15',
        },
      ],
      documents: [
        { name: 'Confidential Information Memorandum (CIM)', type: 'PDF', requiresNda: true },
        { name: 'Financial Statements (3 years)', type: 'PDF', requiresNda: true },
        { name: 'Tax Returns (3 years)', type: 'PDF', requiresNda: true },
        { name: 'Franchise Agreement', type: 'PDF', requiresNda: true },
        { name: 'Lease Agreement', type: 'PDF', requiresNda: true },
        { name: 'Strategic Growth Plan', type: 'PDF', isPremium: true, price: 225, includedInPlans: ['essential', 'essential-plus'] },
        { name: 'Market & Competition Analysis Report', type: 'PDF', isPremium: true, price: 170, includedInPlans: ['essential-plus'] },
        { name: 'Custom Market Study', type: 'PDF', isPremium: true, price: 295, includedInPlans: [] },
      ],
      broker: {
        name: 'Jean-Pierre Tremblay',
        company: 'AcquizGroup',
        phone: '(514) 555-1234',
        email: 'jp.tremblay@acquizgroup.com',
        image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=150&h=150&fit=crop',
      },
    },
    fr: {
      title: 'Franchise de Restauration Rapide Établie',
      location: 'Montréal, QC, Canada',
      badges: [
        { label: 'Seller Financing', type: 'green' },
        { label: '🇨🇦 Canada', type: 'gold' },
      ],
      mainImage: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1200&h=600&fit=crop',
      thumbnails: [
        'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=200&h=150&fit=crop',
        'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=200&h=150&fit=crop',
        'https://images.unsplash.com/photo-1559329007-40df8a9345d8?w=200&h=150&fit=crop',
        'https://images.unsplash.com/photo-1571997478779-2adcbbe9ab2f?w=200&h=150&fit=crop',
      ],
      financials: {
        askingPrice: '$850,000',
        cashFlow: '$185,000',
        grossRevenue: '$1,200,000',
        sde: '$210,000',
        ebitda: '$195,000',
        realEstate: 'Location',
        established: '2008',
        ffe: '$120,000',
        inventory: 'Inclus dans le prix',
      },
      description: [
        "Franchise bien établie dans un emplacement premium avec fort achalandage au cœur de Montréal. Cette opportunité exceptionnelle offre plus de 15 ans d'opération continue avec une équipe stable de 18 employés dévoués.",
        "L'entreprise bénéficie d'une excellente réputation locale et d'un système opérationnel éprouvé. Le restaurant est situé dans une zone commerciale très fréquentée avec un parking amplement disponible. Les revenus sont diversifiés entre les services sur place, à emporter et la livraison via plusieurs plateformes populaires.",
        "Cette franchise offre une opportunité de croissance significative avec l'expansion des services de livraison et la possibilité d'étendre les heures d'ouverture. Le propriétaire actuel fournira une formation complète et un soutien pendant la transition.",
      ],
      detailedInfo: [
        { label: 'Emplacement', value: 'Montréal, QC' },
        { label: 'Inventaire', value: 'Inclus dans le prix' },
        { label: 'Immobilier', value: 'Location' },
        { label: 'Superficie (pi²)', value: '2,500' },
        { label: 'Durée du Bail', value: '5 ans restants' },
        { label: 'Année Établie', value: '2008' },
        { label: 'Équipement (FF&E)', value: '$120,000' },
        { label: 'Installations', value: 'Cuisine complète' },
        { label: 'Croissance & Expansion', value: 'Fort potentiel' },
        { label: 'Soutien & Formation', value: 'Formation complète incluse' },
        { label: 'Raison de Vente', value: 'Retraite' },
        { label: "Nombre d'Employés", value: '18' },
      ],
      financingOptions: [
        {
          title: 'Financement Vendeur',
          details: "Jusqu'à 30% du prix de vente",
          lenderType: 'Prêteur Privé',
          loanProductType: 'Note du Vendeur',
          loanAmountOffered: 225000,
          loanTermLengthMonths: 60,
          interestRateType: 'fixed',
          interestRateValue: 6.5,
          amortizationType: 'linear',
          prepaymentPenalty: 'Aucune après 12 mois',
          maintenanceCovenantsRequired: false,
          dscrMinimumRequired: 1.25,
          dscrCalculationBase: 'sde',
          collateralRequired: true,
          collateralRequirements: ['All Business Assets (First Lien)'],
          personalGuaranteeRequired: true,
          realEstateConditioned: false,
          creditScoreRequired: true,
          creditScoreMinimum: 680,
          acceptForeignCreditScores: false,
          estimatedClosingTimelineDays: 30,
          offerExpirationDate: '2026-06-30',
        },
        {
          title: 'Prêt SBA Pré-Approuvé',
          details: 'Éligible au financement SBA 7(a)',
          lenderType: 'Banque Commerciale',
          loanProductType: 'SBA 7(a)',
          loanAmountOffered: 600000,
          loanTermLengthMonths: 120,
          interestRateType: 'floating',
          interestRateValue: 7.25,
          interestRateMarginBps: 275,
          interestRateFloor: 5.0,
          amortizationType: 'linear',
          prepaymentPenalty: '5% an 1, 3% an 2, 1% an 3',
          arrangementFeePercent: 2.0,
          maintenanceCovenantsRequired: true,
          netDebtToEBITDAMaxAllowed: 4.0,
          dscrMinimumRequired: 1.15,
          dscrCalculationBase: 'ebitda',
          dividendRestrictions: 'Max 50% du revenu net',
          collateralRequired: true,
          collateralRequirements: ['All Business Assets (First Lien)', 'Equipment & Machinery', 'Personal Guarantee (Secured)'],
          personalGuaranteeRequired: true,
          realEstateConditioned: false,
          creditScoreRequired: true,
          creditScoreMinimum: 680,
          acceptForeignCreditScores: true,
          foreignCreditScoreMinimum: 700,
          acceptedForeignCreditCountries: ['Canada', 'Royaume-Uni'],
          acceptForeignCollateral: false,
          estimatedClosingTimelineDays: 60,
          offerExpirationDate: '2026-07-15',
        },
        {
          title: 'Financement Conventionnel',
          details: 'Partenaires financiers disponibles',
          lenderType: 'Banque Commerciale',
          loanProductType: 'Prêt à Terme',
          loanAmountOffered: 500000,
          loanTermLengthMonths: 84,
          interestRateType: 'fixed',
          interestRateValue: 8.0,
          amortizationType: 'linear',
          arrangementFeePercent: 1.5,
          exitFeePercent: 0.5,
          maintenanceCovenantsRequired: true,
          dscrMinimumRequired: 1.3,
          dscrCalculationBase: 'cfads',
          collateralRequired: true,
          collateralRequirements: ['All Business Assets (First Lien)', 'Accounts Receivable'],
          personalGuaranteeRequired: true,
          realEstateConditioned: false,
          creditScoreRequired: true,
          creditScoreMinimum: 700,
          acceptForeignCreditScores: false,
          estimatedClosingTimelineDays: 45,
          offerExpirationDate: '2026-06-15',
        },
      ],
      documents: [
        { name: "Mémorandum d'Information Confidentiel (CIM)", type: 'PDF', requiresNda: true },
        { name: 'États Financiers (3 ans)', type: 'PDF', requiresNda: true },
        { name: 'Déclarations Fiscales (3 ans)', type: 'PDF', requiresNda: true },
        { name: 'Contrat de Franchise', type: 'PDF', requiresNda: true },
        { name: 'Contrat de Bail', type: 'PDF', requiresNda: true },
        { name: 'Plan de Croissance Stratégique', type: 'PDF', isPremium: true, price: 225, includedInPlans: ['essential', 'essential-plus'] },
        { name: "Rapport d'Analyse de Marché et de Concurrence", type: 'PDF', isPremium: true, price: 170, includedInPlans: ['essential-plus'] },
        { name: 'Étude de marché personnalisée', type: 'PDF', isPremium: true, price: 295, includedInPlans: [] },
      ],
      broker: {
        name: 'Jean-Pierre Tremblay',
        company: 'AcquizGroup',
        phone: '(514) 555-1234',
        email: 'jp.tremblay@acquizgroup.com',
        image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=150&h=150&fit=crop',
      },
    },
  },
  // listing-002: SaaS Platform
  {
    id: 'listing-002',
    slug: 'saas-platform',
    en: {
      title: 'B2B Project Management SaaS Platform',
      location: 'Austin, TX, United States',
      badges: [
        { label: 'TECHNOLOGY', type: 'gold' },
        { label: '✓ E2 Visa Eligible', type: 'blue' },
      ],
      mainImage: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=1200&h=600&fit=crop',
      thumbnails: [
        'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=200&h=150&fit=crop',
        'https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=200&h=150&fit=crop',
        'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=200&h=150&fit=crop',
        'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=200&h=150&fit=crop',
      ],
      financials: {
        askingPrice: '$2,400,000',
        cashFlow: '$520,000',
        grossRevenue: '$1,140,000',
        sde: '$545,000',
        ebitda: '$530,000',
        realEstate: 'Remote office',
        established: '2018',
        ffe: 'N/A',
        inventory: 'N/A',
      },
      description: [
        'Profitable and rapidly growing B2B SaaS platform specializing in project management for medium-sized businesses. With over 850 active clients across the United States, the platform generates monthly recurring revenues (MRR) of $95K.',
        'The company stands out with an exceptional retention rate of 94%, demonstrating the quality of the product and customer service. The modern and scalable technology is built on robust cloud architecture with a dedicated technical team of 8 people. The product is regularly updated with new features based on customer feedback.',
        'This opportunity represents an ideal investment for an acquirer looking to enter the B2B SaaS market with a solid customer base, predictable recurring revenues, and strong growth potential through geographic expansion and adding new features.',
      ],
      detailedInfo: [
        { label: 'Location', value: 'Austin, TX' },
        { label: 'Inventory', value: 'Not applicable' },
        { label: 'Real Estate', value: 'Remote office' },
        { label: 'Active Clients', value: '850+' },
        { label: 'MRR (Monthly Recurring Revenue)', value: '$95,000' },
        { label: 'Year Established', value: '2018' },
        { label: 'Retention Rate', value: '94%' },
        { label: 'Technology', value: 'Modern cloud, scalable' },
        { label: 'Growth & Expansion', value: 'International expansion possible' },
        { label: 'Support & Training', value: '6 months transition included' },
        { label: 'Reason for Sale', value: 'Pursue other projects' },
        { label: 'Number of Employees', value: '8 (technical team)' },
      ],
      financingOptions: [
        {
          title: 'SBA Loan Pre-Approved',
          details: 'Eligible for SBA 7(a) financing',
          lenderType: 'Commercial Bank',
          loanProductType: 'SBA 7(a)',
          loanAmountOffered: 1200000,
          loanTermLengthMonths: 120,
          interestRateType: 'floating',
          interestRateValue: 6.75,
          interestRateMarginBps: 250,
          interestRateFloor: 4.5,
          amortizationType: 'linear',
          arrangementFeePercent: 1.75,
          maintenanceCovenantsRequired: true,
          dscrMinimumRequired: 1.2,
          dscrCalculationBase: 'ebitda',
          collateralRequired: true,
          collateralRequirements: ['All Business Assets (First Lien)', 'Intellectual Property'],
          personalGuaranteeRequired: true,
          realEstateConditioned: false,
          creditScoreRequired: true,
          creditScoreMinimum: 700,
          acceptForeignCreditScores: true,
          foreignCreditScoreMinimum: 720,
          acceptedForeignCreditCountries: ['Canada', 'UK', 'Germany'],
          estimatedClosingTimelineDays: 60,
          offerExpirationDate: '2026-07-30',
        },
        {
          title: 'Conventional Financing',
          details: 'Financing partners available',
          lenderType: 'Commercial Bank',
          loanProductType: 'Term Loan',
          loanAmountOffered: 1000000,
          loanTermLengthMonths: 84,
          interestRateType: 'fixed',
          interestRateValue: 7.5,
          amortizationType: 'linear',
          arrangementFeePercent: 1.5,
          exitFeePercent: 0.75,
          maintenanceCovenantsRequired: true,
          dscrMinimumRequired: 1.25,
          dscrCalculationBase: 'cfads',
          collateralRequired: true,
          collateralRequirements: ['All Business Assets (First Lien)', 'Equipment & Machinery'],
          personalGuaranteeRequired: true,
          creditScoreRequired: true,
          creditScoreMinimum: 720,
          estimatedClosingTimelineDays: 45,
          offerExpirationDate: '2026-06-30',
        },
        {
          title: 'Earnout Structure',
          details: 'Performance-based payment structure available',
          lenderType: 'Private Lender',
          loanProductType: 'Mezzanine / Earnout',
          loanAmountOffered: 500000,
          loanTermLengthMonths: 36,
          interestRateType: 'fixed',
          interestRateValue: 10.0,
          amortizationType: 'in_fine',
          isSubordinatedDebt: true,
          subordinationToSeniorDebt: true,
          balloonPaymentYear: 3,
          balloonPaymentAmount: 500000,
          maintenanceCovenantsRequired: false,
          collateralRequired: false,
          personalGuaranteeRequired: false,
          creditScoreRequired: false,
          estimatedClosingTimelineDays: 30,
          offerExpirationDate: '2026-06-15',
        },
      ],
      documents: [
        { name: 'Confidential Information Memorandum (CIM)', type: 'PDF' },
        { name: 'Financial Statements (3 years)', type: 'PDF' },
        { name: 'MRR & Churn Analytics', type: 'PDF' },
        { name: 'Technology Architecture Document', type: 'PDF' },
        { name: 'Customer Base Analysis', type: 'PDF' },
        { name: 'Strategic Growth Plan', type: 'PDF', isPremium: true, price: 225, includedInPlans: ['essential', 'essential-plus'] },
        { name: 'Market & Competition Analysis Report', type: 'PDF', isPremium: true, price: 170, includedInPlans: ['essential-plus'] },
        { name: 'Custom Market Study', type: 'PDF', isPremium: true, price: 295, includedInPlans: [] },
      ],
      broker: {
        name: 'Michael Chen',
        company: 'AcquizGroup',
        phone: '(512) 555-7890',
        email: 'm.chen@acquizgroup.com',
        image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop',
      },
    },
    fr: {
      title: 'Plateforme SaaS B2B de Gestion de Projets',
      location: 'Austin, TX, États-Unis',
      badges: [
        { label: 'TECHNOLOGIE', type: 'gold' },
        { label: '✓ Éligible Visa E2', type: 'blue' },
      ],
      mainImage: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=1200&h=600&fit=crop',
      thumbnails: [
        'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=200&h=150&fit=crop',
        'https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=200&h=150&fit=crop',
        'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=200&h=150&fit=crop',
        'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=200&h=150&fit=crop',
      ],
      financials: {
        askingPrice: '$2,400,000',
        cashFlow: '$520,000',
        grossRevenue: '$1,140,000',
        sde: '$545,000',
        ebitda: '$530,000',
        realEstate: 'Bureau à distance',
        established: '2018',
        ffe: 'Non applicable',
        inventory: 'Non applicable',
      },
      description: [
        'Plateforme SaaS B2B rentable et en forte croissance, spécialisée dans la gestion de projets pour les entreprises de taille moyenne. Avec plus de 850 clients actifs répartis sur tout le territoire américain, la plateforme génère des revenus récurrents mensuels (MRR) de $95K.',
        "L'entreprise se distingue par un taux de rétention exceptionnel de 94%, témoignant de la qualité du produit et du service client. La technologie moderne et scalable repose sur une architecture cloud robuste avec une équipe technique dévouée de 8 personnes. Le produit est régulièrement mis à jour avec de nouvelles fonctionnalités basées sur les retours clients.",
        "Cette opportunité représente un investissement idéal pour un acquéreur cherchant à entrer dans le marché SaaS B2B avec une base solide de clients, des revenus récurrents prévisibles, et un fort potentiel de croissance à travers l'expansion géographique et l'ajout de nouvelles fonctionnalités.",
      ],
      detailedInfo: [
        { label: 'Emplacement', value: 'Austin, TX' },
        { label: 'Inventaire', value: 'Non applicable' },
        { label: 'Immobilier', value: 'Bureau à distance' },
        { label: 'Clients Actifs', value: '850+' },
        { label: 'MRR (Revenus Mensuels Récurrents)', value: '$95,000' },
        { label: 'Année Établie', value: '2018' },
        { label: 'Taux de Rétention', value: '94%' },
        { label: 'Technologie', value: 'Cloud moderne, scalable' },
        { label: 'Croissance & Expansion', value: 'Expansion internationale possible' },
        { label: 'Soutien & Formation', value: '6 mois de transition inclus' },
        { label: 'Raison de Vente', value: "Poursuivre d'autres projets" },
        { label: "Nombre d'Employés", value: '8 (équipe technique)' },
      ],
      financingOptions: [
        {
          title: 'Prêt SBA Pré-Approuvé',
          details: 'Éligible au financement SBA 7(a)',
          lenderType: 'Banque Commerciale',
          loanProductType: 'SBA 7(a)',
          loanAmountOffered: 1200000,
          loanTermLengthMonths: 120,
          interestRateType: 'floating',
          interestRateValue: 6.75,
          interestRateMarginBps: 250,
          interestRateFloor: 4.5,
          amortizationType: 'linear',
          arrangementFeePercent: 1.75,
          maintenanceCovenantsRequired: true,
          dscrMinimumRequired: 1.2,
          dscrCalculationBase: 'ebitda',
          collateralRequired: true,
          collateralRequirements: ['All Business Assets (First Lien)', 'Intellectual Property'],
          personalGuaranteeRequired: true,
          creditScoreRequired: true,
          creditScoreMinimum: 700,
          acceptForeignCreditScores: true,
          foreignCreditScoreMinimum: 720,
          acceptedForeignCreditCountries: ['Canada', 'Royaume-Uni', 'Allemagne'],
          estimatedClosingTimelineDays: 60,
          offerExpirationDate: '2026-07-30',
        },
        {
          title: 'Financement Conventionnel',
          details: 'Partenaires financiers disponibles',
          lenderType: 'Banque Commerciale',
          loanProductType: 'Prêt à Terme',
          loanAmountOffered: 1000000,
          loanTermLengthMonths: 84,
          interestRateType: 'fixed',
          interestRateValue: 7.5,
          amortizationType: 'linear',
          arrangementFeePercent: 1.5,
          exitFeePercent: 0.75,
          maintenanceCovenantsRequired: true,
          dscrMinimumRequired: 1.25,
          dscrCalculationBase: 'cfads',
          collateralRequired: true,
          collateralRequirements: ['All Business Assets (First Lien)', 'Equipment & Machinery'],
          personalGuaranteeRequired: true,
          creditScoreRequired: true,
          creditScoreMinimum: 720,
          estimatedClosingTimelineDays: 45,
          offerExpirationDate: '2026-06-30',
        },
        {
          title: 'Structure Earnout',
          details: 'Structure de paiement basée sur la performance disponible',
          lenderType: 'Prêteur Privé',
          loanProductType: 'Mezzanine / Earnout',
          loanAmountOffered: 500000,
          loanTermLengthMonths: 36,
          interestRateType: 'fixed',
          interestRateValue: 10.0,
          amortizationType: 'in_fine',
          isSubordinatedDebt: true,
          subordinationToSeniorDebt: true,
          balloonPaymentYear: 3,
          balloonPaymentAmount: 500000,
          maintenanceCovenantsRequired: false,
          collateralRequired: false,
          personalGuaranteeRequired: false,
          creditScoreRequired: false,
          estimatedClosingTimelineDays: 30,
          offerExpirationDate: '2026-06-15',
        },
      ],
      documents: [
        { name: "Mémorandum d'Information Confidentiel (CIM)", type: 'PDF' },
        { name: 'États Financiers (3 ans)', type: 'PDF' },
        { name: 'Analytique MRR & Churn', type: 'PDF' },
        { name: "Document d'Architecture Technologique", type: 'PDF' },
        { name: 'Analyse de la Base Client', type: 'PDF' },
        { name: 'Plan de Croissance Stratégique', type: 'PDF', isPremium: true, price: 225, includedInPlans: ['essential', 'essential-plus'] },
        { name: "Rapport d'Analyse de Marché et de Concurrence", type: 'PDF', isPremium: true, price: 170, includedInPlans: ['essential-plus'] },
        { name: 'Étude de marché personnalisée', type: 'PDF', isPremium: true, price: 295, includedInPlans: [] },
      ],
      broker: {
        name: 'Michael Chen',
        company: 'AcquizGroup',
        phone: '(512) 555-7890',
        email: 'm.chen@acquizgroup.com',
        image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop',
      },
    },
  },
  // listing-003: Construction
  {
    id: 'listing-003',
    slug: 'construction-company',
    en: {
      title: 'Residential and Commercial Construction Company',
      location: 'Calgary, AB, Canada',
      badges: [
        { label: 'Seller Financing', type: 'green' },
        { label: '🇨🇦 Canada', type: 'gold' },
      ],
      mainImage: 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=1200&h=600&fit=crop',
      thumbnails: [
        'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=200&h=150&fit=crop',
        'https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=200&h=150&fit=crop',
        'https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=200&h=150&fit=crop',
        'https://images.unsplash.com/photo-1590069261209-f8e9b8642343?w=200&h=150&fit=crop',
      ],
      financials: {
        askingPrice: '$1,650,000',
        cashFlow: '$380,000',
        grossRevenue: '$4,200,000',
        sde: '$395,000',
        ebitda: '$385,000',
        realEstate: 'Owned (warehouse)',
        established: '2002',
        ffe: '$400,000',
        inventory: '$85,000',
      },
      description: [
        'Construction company established for 22 years with a diversified portfolio of residential and commercial projects. The company has built an excellent local reputation for quality and reliability.',
        'Modern equipment included valued at $400K with contracts signed for the next 18 months. The experienced team and proven processes ensure consistent project delivery and customer satisfaction.',
        'Significant growth opportunities exist through expanded geographic coverage and adding specialized services. The owner will provide transition support to ensure continuity.',
      ],
      detailedInfo: [
        { label: 'Location', value: 'Calgary, AB' },
        { label: 'Inventory', value: '$85,000 (materials)' },
        { label: 'Real Estate', value: 'Owned warehouse' },
        { label: 'Square Footage', value: '8,000 sq ft' },
        { label: 'Contract Backlog', value: '18 months' },
        { label: 'Year Established', value: '2002' },
        { label: 'Equipment (FF&E)', value: '$400,000' },
        { label: 'Certifications', value: 'Licensed & Bonded' },
        { label: 'Growth & Expansion', value: 'Geographic expansion' },
        { label: 'Support & Training', value: '3 months transition' },
        { label: 'Reason for Sale', value: 'Retirement' },
        { label: 'Number of Employees', value: '25' },
      ],
      financingOptions: [
        {
          title: 'Seller Financing',
          details: 'Up to 25% of the sale price',
          lenderType: 'Private Lender',
          loanProductType: 'Seller Note',
          loanAmountOffered: 187500,
          loanTermLengthMonths: 48,
          interestRateType: 'fixed',
          interestRateValue: 5.5,
          amortizationType: 'linear',
          collateralRequired: true,
          collateralRequirements: ['All Business Assets (First Lien)'],
          personalGuaranteeRequired: true,
          creditScoreRequired: true,
          creditScoreMinimum: 650,
          estimatedClosingTimelineDays: 21,
          offerExpirationDate: '2026-06-30',
        },
        {
          title: 'Bank Financing',
          details: 'Pre-qualified with local banks',
          lenderType: 'Commercial Bank',
          loanProductType: 'Term Loan',
          loanAmountOffered: 550000,
          loanTermLengthMonths: 84,
          interestRateType: 'fixed',
          interestRateValue: 7.0,
          amortizationType: 'linear',
          arrangementFeePercent: 1.25,
          maintenanceCovenantsRequired: true,
          dscrMinimumRequired: 1.2,
          dscrCalculationBase: 'ebitda',
          collateralRequired: true,
          collateralRequirements: ['All Business Assets (First Lien)', 'Equipment & Machinery', 'Business Real Estate (First Lien)'],
          personalGuaranteeRequired: true,
          realEstateConditioned: true,
          creditScoreRequired: true,
          creditScoreMinimum: 700,
          estimatedClosingTimelineDays: 45,
          offerExpirationDate: '2026-07-15',
        },
        {
          title: 'Equipment Financing',
          details: 'Separate financing available for equipment',
          lenderType: 'Specialty Lender',
          loanProductType: 'Equipment Loan',
          loanAmountOffered: 200000,
          loanTermLengthMonths: 60,
          interestRateType: 'fixed',
          interestRateValue: 8.5,
          amortizationType: 'linear',
          collateralRequired: true,
          collateralRequirements: ['Equipment & Machinery'],
          personalGuaranteeRequired: false,
          creditScoreRequired: true,
          creditScoreMinimum: 680,
          estimatedClosingTimelineDays: 14,
          offerExpirationDate: '2026-06-15',
        },
      ],
      documents: [
        { name: 'Confidential Information Memorandum (CIM)', type: 'PDF' },
        { name: 'Financial Statements (3 years)', type: 'PDF' },
        { name: 'Project Portfolio', type: 'PDF' },
        { name: 'Equipment List & Valuation', type: 'PDF' },
        { name: 'Contract Backlog Summary', type: 'PDF' },
      ],
      broker: {
        name: 'Sarah Williams',
        company: 'AcquizGroup',
        phone: '(403) 555-4567',
        email: 's.williams@acquizgroup.com',
        image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=150&h=150&fit=crop',
      },
    },
    fr: {
      title: 'Entreprise de Construction Résidentielle et Commerciale',
      location: 'Calgary, AB, Canada',
      badges: [
        { label: 'Seller Financing', type: 'green' },
        { label: '🇨🇦 Canada', type: 'gold' },
      ],
      mainImage: 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=1200&h=600&fit=crop',
      thumbnails: [
        'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=200&h=150&fit=crop',
        'https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=200&h=150&fit=crop',
        'https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=200&h=150&fit=crop',
        'https://images.unsplash.com/photo-1590069261209-f8e9b8642343?w=200&h=150&fit=crop',
      ],
      financials: {
        askingPrice: '$1,650,000',
        cashFlow: '$380,000',
        grossRevenue: '$4,200,000',
        sde: '$395,000',
        ebitda: '$385,000',
        realEstate: 'Propriété (entrepôt)',
        established: '2002',
        ffe: '$400,000',
        inventory: '$85,000',
      },
      description: [
        "Entreprise de construction établie depuis 22 ans avec un portfolio de projets diversifié entre le résidentiel et le commercial. L'entreprise a bâti une excellente réputation locale pour sa qualité et sa fiabilité.",
        "Équipement moderne inclus d'une valeur de $400K avec des contrats signés pour les 18 prochains mois. L'équipe expérimentée et les processus éprouvés assurent une livraison de projet cohérente et la satisfaction des clients.",
        "Des opportunités de croissance significatives existent grâce à l'expansion géographique et l'ajout de services spécialisés. Le propriétaire fournira un soutien de transition pour assurer la continuité.",
      ],
      detailedInfo: [
        { label: 'Emplacement', value: 'Calgary, AB' },
        { label: 'Inventaire', value: '$85,000 (matériaux)' },
        { label: 'Immobilier', value: 'Entrepôt en propriété' },
        { label: 'Superficie (pi²)', value: '8,000' },
        { label: 'Carnet de Commandes', value: '18 mois' },
        { label: 'Année Établie', value: '2002' },
        { label: 'Équipement (FF&E)', value: '$400,000' },
        { label: 'Certifications', value: 'Licencié & Cautionné' },
        { label: 'Croissance & Expansion', value: 'Expansion géographique' },
        { label: 'Soutien & Formation', value: '3 mois de transition' },
        { label: 'Raison de Vente', value: 'Retraite' },
        { label: "Nombre d'Employés", value: '25' },
      ],
      financingOptions: [
        {
          title: 'Financement Vendeur',
          details: "Jusqu'à 25% du prix de vente",
          lenderType: 'Prêteur Privé',
          loanProductType: 'Note du Vendeur',
          loanAmountOffered: 187500,
          loanTermLengthMonths: 48,
          interestRateType: 'fixed',
          interestRateValue: 5.5,
          amortizationType: 'linear',
          collateralRequired: true,
          collateralRequirements: ['All Business Assets (First Lien)'],
          personalGuaranteeRequired: true,
          creditScoreRequired: true,
          creditScoreMinimum: 650,
          estimatedClosingTimelineDays: 21,
          offerExpirationDate: '2026-06-30',
        },
        {
          title: 'Financement Bancaire',
          details: 'Pré-qualifié auprès des banques locales',
          lenderType: 'Banque Commerciale',
          loanProductType: 'Prêt à Terme',
          loanAmountOffered: 550000,
          loanTermLengthMonths: 84,
          interestRateType: 'fixed',
          interestRateValue: 7.0,
          amortizationType: 'linear',
          arrangementFeePercent: 1.25,
          maintenanceCovenantsRequired: true,
          dscrMinimumRequired: 1.2,
          dscrCalculationBase: 'ebitda',
          collateralRequired: true,
          collateralRequirements: ['All Business Assets (First Lien)', 'Equipment & Machinery', 'Business Real Estate (First Lien)'],
          personalGuaranteeRequired: true,
          realEstateConditioned: true,
          creditScoreRequired: true,
          creditScoreMinimum: 700,
          estimatedClosingTimelineDays: 45,
          offerExpirationDate: '2026-07-15',
        },
        {
          title: "Financement d'Équipement",
          details: "Financement séparé disponible pour l'équipement",
          lenderType: 'Prêteur Spécialisé',
          loanProductType: "Prêt d'Équipement",
          loanAmountOffered: 200000,
          loanTermLengthMonths: 60,
          interestRateType: 'fixed',
          interestRateValue: 8.5,
          amortizationType: 'linear',
          collateralRequired: true,
          collateralRequirements: ['Equipment & Machinery'],
          personalGuaranteeRequired: false,
          creditScoreRequired: true,
          creditScoreMinimum: 680,
          estimatedClosingTimelineDays: 14,
          offerExpirationDate: '2026-06-15',
        },
      ],
      documents: [
        { name: "Mémorandum d'Information Confidentiel (CIM)", type: 'PDF' },
        { name: 'États Financiers (3 ans)', type: 'PDF' },
        { name: 'Portfolio de Projets', type: 'PDF' },
        { name: "Liste d'Équipement & Évaluation", type: 'PDF' },
        { name: 'Résumé du Carnet de Commandes', type: 'PDF' },
      ],
      broker: {
        name: 'Sarah Williams',
        company: 'AcquizGroup',
        phone: '(403) 555-4567',
        email: 's.williams@acquizgroup.com',
        image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=150&h=150&fit=crop',
      },
    },
  },
  // listing-004: Dental Clinic
  {
    id: 'listing-004',
    slug: 'dental-clinic',
    en: {
      title: 'Multi-Practitioner Dental Clinic',
      location: 'Denver, CO, United States',
      badges: [
        { label: 'HEALTHCARE', type: 'gold' },
        { label: '✓ E2 Visa Eligible', type: 'blue' },
      ],
      mainImage: 'https://images.unsplash.com/photo-1629909615957-be38e0b46e75?w=1200&h=600&fit=crop',
      thumbnails: [
        'https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?w=200&h=150&fit=crop',
        'https://images.unsplash.com/photo-1606811841689-23dfddce3e95?w=200&h=150&fit=crop',
        'https://images.unsplash.com/photo-1609840114035-3c981b782dfe?w=200&h=150&fit=crop',
        'https://images.unsplash.com/photo-1445527815219-ecbfec67492e?w=200&h=150&fit=crop',
      ],
      financials: {
        askingPrice: '$1,200,000',
        cashFlow: '$290,000',
        grossRevenue: '$1,800,000',
        sde: '$305,000',
        ebitda: '$295,000',
        realEstate: 'Leased',
        established: '2015',
        ffe: '$350,000',
        inventory: '$25,000',
      },
      description: [
        'Modern dental clinic with 3 dentists and support staff. Loyal patient base of 2,500+ with state-of-the-art equipment. Strategic location with parking and consistent annual growth of 8%.',
        'The clinic offers comprehensive dental services including general dentistry, cosmetic procedures, and orthodontics. The modern facility features digital X-rays, CEREC technology, and a comfortable patient environment.',
        'Excellent opportunity for a dental professional looking to own an established practice with strong recurring revenue and growth potential through extended hours and additional services.',
      ],
      detailedInfo: [
        { label: 'Location', value: 'Denver, CO' },
        { label: 'Inventory', value: '$25,000 (supplies)' },
        { label: 'Real Estate', value: 'Leased' },
        { label: 'Square Footage', value: '3,200 sq ft' },
        { label: 'Patient Base', value: '2,500+' },
        { label: 'Year Established', value: '2015' },
        { label: 'Equipment (FF&E)', value: '$350,000' },
        { label: 'Technology', value: 'Digital X-rays, CEREC' },
        { label: 'Growth Rate', value: '8% annually' },
        { label: 'Support & Training', value: '6 months transition' },
        { label: 'Reason for Sale', value: 'Relocation' },
        { label: 'Number of Employees', value: '12' },
      ],
      financingOptions: [
        {
          title: 'Healthcare Practice Loan',
          details: 'Specialized healthcare financing available',
          lenderType: 'Specialty Lender',
          loanProductType: 'Healthcare Practice Loan',
          loanAmountOffered: 450000,
          loanTermLengthMonths: 84,
          interestRateType: 'fixed',
          interestRateValue: 6.25,
          amortizationType: 'linear',
          arrangementFeePercent: 1.0,
          maintenanceCovenantsRequired: true,
          dscrMinimumRequired: 1.3,
          dscrCalculationBase: 'sde',
          collateralRequired: true,
          collateralRequirements: ['All Business Assets (First Lien)', 'Equipment & Machinery'],
          personalGuaranteeRequired: true,
          creditScoreRequired: true,
          creditScoreMinimum: 700,
          estimatedClosingTimelineDays: 45,
          offerExpirationDate: '2026-07-31',
        },
        {
          title: 'SBA 7(a) Loan',
          details: 'Pre-qualified for SBA financing',
          lenderType: 'Commercial Bank',
          loanProductType: 'SBA 7(a)',
          loanAmountOffered: 500000,
          loanTermLengthMonths: 120,
          interestRateType: 'floating',
          interestRateValue: 7.0,
          interestRateMarginBps: 275,
          amortizationType: 'linear',
          arrangementFeePercent: 2.0,
          maintenanceCovenantsRequired: true,
          dscrMinimumRequired: 1.15,
          dscrCalculationBase: 'ebitda',
          collateralRequired: true,
          collateralRequirements: ['All Business Assets (First Lien)', 'Personal Guarantee (Secured)'],
          personalGuaranteeRequired: true,
          creditScoreRequired: true,
          creditScoreMinimum: 680,
          acceptForeignCreditScores: true,
          foreignCreditScoreMinimum: 700,
          estimatedClosingTimelineDays: 60,
          offerExpirationDate: '2026-07-15',
        },
        {
          title: 'Equipment Financing',
          details: 'Separate financing for equipment updates',
          lenderType: 'Specialty Lender',
          loanProductType: 'Equipment Loan',
          loanAmountOffered: 150000,
          loanTermLengthMonths: 60,
          interestRateType: 'fixed',
          interestRateValue: 8.0,
          amortizationType: 'linear',
          collateralRequired: true,
          collateralRequirements: ['Equipment & Machinery'],
          personalGuaranteeRequired: false,
          creditScoreRequired: true,
          creditScoreMinimum: 660,
          estimatedClosingTimelineDays: 14,
          offerExpirationDate: '2026-06-30',
        },
      ],
      documents: [
        { name: 'Confidential Information Memorandum (CIM)', type: 'PDF' },
        { name: 'Financial Statements (3 years)', type: 'PDF' },
        { name: 'Patient Demographics Report', type: 'PDF' },
        { name: 'Equipment List & Condition Report', type: 'PDF' },
        { name: 'Lease Agreement', type: 'PDF' },
      ],
      broker: {
        name: 'Robert Martinez',
        company: 'AcquizGroup',
        phone: '(303) 555-8901',
        email: 'r.martinez@acquizgroup.com',
        image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop',
      },
    },
    fr: {
      title: 'Clinique Dentaire Multi-Praticiens',
      location: 'Denver, CO, États-Unis',
      badges: [
        { label: 'SANTÉ', type: 'gold' },
        { label: '✓ Éligible Visa E2', type: 'blue' },
      ],
      mainImage: 'https://images.unsplash.com/photo-1629909615957-be38e0b46e75?w=1200&h=600&fit=crop',
      thumbnails: [
        'https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?w=200&h=150&fit=crop',
        'https://images.unsplash.com/photo-1606811841689-23dfddce3e95?w=200&h=150&fit=crop',
        'https://images.unsplash.com/photo-1609840114035-3c981b782dfe?w=200&h=150&fit=crop',
        'https://images.unsplash.com/photo-1445527815219-ecbfec67492e?w=200&h=150&fit=crop',
      ],
      financials: {
        askingPrice: '$1,200,000',
        cashFlow: '$290,000',
        grossRevenue: '$1,800,000',
        sde: '$305,000',
        ebitda: '$295,000',
        realEstate: 'Location',
        established: '2015',
        ffe: '$350,000',
        inventory: '$25,000',
      },
      description: [
        "Clinique dentaire moderne avec 3 dentistes et personnel de soutien. Base de patients fidèles de 2,500+. Équipement à la fine pointe. Emplacement stratégique avec parking. Croissance annuelle constante de 8%.",
        "La clinique offre des services dentaires complets incluant la dentisterie générale, les procédures esthétiques et l'orthodontie. L'établissement moderne dispose de radiographies numériques, de la technologie CEREC et d'un environnement confortable pour les patients.",
        "Excellente opportunité pour un professionnel dentaire cherchant à posséder une pratique établie avec des revenus récurrents solides et un potentiel de croissance grâce à des heures prolongées et des services additionnels.",
      ],
      detailedInfo: [
        { label: 'Emplacement', value: 'Denver, CO' },
        { label: 'Inventaire', value: '$25,000 (fournitures)' },
        { label: 'Immobilier', value: 'Location' },
        { label: 'Superficie (pi²)', value: '3,200' },
        { label: 'Base de Patients', value: '2,500+' },
        { label: 'Année Établie', value: '2015' },
        { label: 'Équipement (FF&E)', value: '$350,000' },
        { label: 'Technologie', value: 'Rayons-X numériques, CEREC' },
        { label: 'Taux de Croissance', value: '8% annuellement' },
        { label: 'Soutien & Formation', value: '6 mois de transition' },
        { label: 'Raison de Vente', value: 'Déménagement' },
        { label: "Nombre d'Employés", value: '12' },
      ],
      financingOptions: [
        {
          title: 'Prêt Pratique de Santé',
          details: 'Financement spécialisé en santé disponible',
          lenderType: 'Prêteur Spécialisé',
          loanProductType: 'Prêt Pratique de Santé',
          loanAmountOffered: 450000,
          loanTermLengthMonths: 84,
          interestRateType: 'fixed',
          interestRateValue: 6.25,
          amortizationType: 'linear',
          arrangementFeePercent: 1.0,
          maintenanceCovenantsRequired: true,
          dscrMinimumRequired: 1.3,
          dscrCalculationBase: 'sde',
          collateralRequired: true,
          collateralRequirements: ['All Business Assets (First Lien)', 'Equipment & Machinery'],
          personalGuaranteeRequired: true,
          creditScoreRequired: true,
          creditScoreMinimum: 700,
          estimatedClosingTimelineDays: 45,
          offerExpirationDate: '2026-07-31',
        },
        {
          title: 'Prêt SBA 7(a)',
          details: 'Pré-qualifié pour financement SBA',
          lenderType: 'Banque Commerciale',
          loanProductType: 'SBA 7(a)',
          loanAmountOffered: 500000,
          loanTermLengthMonths: 120,
          interestRateType: 'floating',
          interestRateValue: 7.0,
          interestRateMarginBps: 275,
          amortizationType: 'linear',
          arrangementFeePercent: 2.0,
          maintenanceCovenantsRequired: true,
          dscrMinimumRequired: 1.15,
          dscrCalculationBase: 'ebitda',
          collateralRequired: true,
          collateralRequirements: ['All Business Assets (First Lien)', 'Personal Guarantee (Secured)'],
          personalGuaranteeRequired: true,
          creditScoreRequired: true,
          creditScoreMinimum: 680,
          acceptForeignCreditScores: true,
          foreignCreditScoreMinimum: 700,
          estimatedClosingTimelineDays: 60,
          offerExpirationDate: '2026-07-15',
        },
        {
          title: "Financement d'Équipement",
          details: "Financement séparé pour mises à jour d'équipement",
          lenderType: 'Prêteur Spécialisé',
          loanProductType: "Prêt d'Équipement",
          loanAmountOffered: 150000,
          loanTermLengthMonths: 60,
          interestRateType: 'fixed',
          interestRateValue: 8.0,
          amortizationType: 'linear',
          collateralRequired: true,
          collateralRequirements: ['Equipment & Machinery'],
          personalGuaranteeRequired: false,
          creditScoreRequired: true,
          creditScoreMinimum: 660,
          estimatedClosingTimelineDays: 14,
          offerExpirationDate: '2026-06-30',
        },
      ],
      documents: [
        { name: "Mémorandum d'Information Confidentiel (CIM)", type: 'PDF' },
        { name: 'États Financiers (3 ans)', type: 'PDF' },
        { name: 'Rapport Démographique des Patients', type: 'PDF' },
        { name: "Liste d'Équipement & Rapport de Condition", type: 'PDF' },
        { name: 'Contrat de Bail', type: 'PDF' },
      ],
      broker: {
        name: 'Robert Martinez',
        company: 'AcquizGroup',
        phone: '(303) 555-8901',
        email: 'r.martinez@acquizgroup.com',
        image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop',
      },
    },
  },
  // listing-005: E-commerce
  {
    id: 'listing-005',
    slug: 'ecommerce-beauty',
    en: {
      title: 'E-Commerce - Natural Beauty Products',
      location: 'Vancouver, BC, Canada',
      badges: [
        { label: 'Seller Financing', type: 'green' },
        { label: '🇨🇦 Canada', type: 'gold' },
      ],
      mainImage: 'https://images.unsplash.com/photo-1596462502278-27bfdc403348?w=1200&h=600&fit=crop',
      thumbnails: [
        'https://images.unsplash.com/photo-1556228720-195a672e8a03?w=200&h=150&fit=crop',
        'https://images.unsplash.com/photo-1612817288484-6f916006741a?w=200&h=150&fit=crop',
        'https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?w=200&h=150&fit=crop',
        'https://images.unsplash.com/photo-1608248543803-ba4f8c70ae0b?w=200&h=150&fit=crop',
      ],
      financials: {
        askingPrice: '$450,000',
        cashFlow: '$125,000',
        grossRevenue: '$850,000',
        sde: '$130,000',
        ebitda: '$120,000',
        realEstate: 'Home-based',
        established: '2019',
        ffe: '$15,000',
        inventory: '$45,000',
      },
      description: [
        'Established e-commerce business with a strong brand and loyal customer base. Over 15,000 annual orders with presence on Amazon and Shopify store. Inventory included. Gross margins of 65%. Advanced automation.',
        'The brand has built a strong social media following with over 50K followers across platforms. Products are sourced from certified suppliers with exclusive formulations. Customer retention rate of 40%.',
        'Ideal opportunity for an entrepreneur looking to enter the growing natural beauty market with an established brand and proven business model.',
      ],
      detailedInfo: [
        { label: 'Location', value: 'Vancouver, BC' },
        { label: 'Inventory', value: '$45,000 included' },
        { label: 'Real Estate', value: 'Home-based' },
        { label: 'Annual Orders', value: '15,000+' },
        { label: 'Gross Margin', value: '65%' },
        { label: 'Year Established', value: '2019' },
        { label: 'Social Following', value: '50K+' },
        { label: 'Platforms', value: 'Amazon, Shopify' },
        { label: 'Customer Retention', value: '40%' },
        { label: 'Support & Training', value: '3 months transition' },
        { label: 'Reason for Sale', value: 'New venture' },
        { label: 'Number of Employees', value: '2 part-time' },
      ],
      financingOptions: [
        {
          title: 'Seller Financing',
          details: 'Up to 40% with approved buyer',
          lenderType: 'Private Lender',
          loanProductType: 'Seller Note',
          loanAmountOffered: 100000,
          loanTermLengthMonths: 36,
          interestRateType: 'fixed',
          interestRateValue: 5.0,
          amortizationType: 'linear',
          collateralRequired: true,
          collateralRequirements: ['All Business Assets (First Lien)', 'Inventory'],
          personalGuaranteeRequired: true,
          creditScoreRequired: true,
          creditScoreMinimum: 650,
          estimatedClosingTimelineDays: 21,
          offerExpirationDate: '2026-06-30',
        },
        {
          title: 'E-commerce Lender',
          details: 'Specialized e-commerce financing',
          lenderType: 'Specialty Lender',
          loanProductType: 'Revenue-Based Financing',
          loanAmountOffered: 175000,
          loanTermLengthMonths: 48,
          interestRateType: 'fixed',
          interestRateValue: 9.0,
          amortizationType: 'linear',
          arrangementFeePercent: 2.5,
          maintenanceCovenantsRequired: false,
          collateralRequired: true,
          collateralRequirements: ['All Business Assets (First Lien)', 'Accounts Receivable', 'Intellectual Property'],
          personalGuaranteeRequired: false,
          creditScoreRequired: true,
          creditScoreMinimum: 680,
          acceptForeignCreditScores: true,
          foreignCreditScoreMinimum: 700,
          acceptedForeignCreditCountries: ['Canada', 'UK', 'Australia'],
          estimatedClosingTimelineDays: 14,
          offerExpirationDate: '2026-07-15',
        },
        {
          title: 'Inventory Financing',
          details: 'Separate inventory financing available',
          lenderType: 'Specialty Lender',
          loanProductType: 'ABL - Inventory Line',
          loanAmountOffered: 80000,
          loanTermLengthMonths: 12,
          interestRateType: 'floating',
          interestRateValue: 8.5,
          interestRateMarginBps: 300,
          amortizationType: 'linear',
          isABLOffer: true,
          ABL_Inventory_EligiblityPercent: 60,
          ABL_Cash_Sweep: true,
          drawFeePercentABL: 0.25,
          collateralRequired: true,
          collateralRequirements: ['Inventory'],
          personalGuaranteeRequired: false,
          creditScoreRequired: true,
          creditScoreMinimum: 660,
          estimatedClosingTimelineDays: 10,
          offerExpirationDate: '2026-06-15',
        },
      ],
      documents: [
        { name: 'Confidential Information Memorandum (CIM)', type: 'PDF' },
        { name: 'Financial Statements (3 years)', type: 'PDF' },
        { name: 'Amazon & Shopify Analytics', type: 'PDF' },
        { name: 'Inventory Valuation Report', type: 'PDF' },
        { name: 'Supplier Agreements', type: 'PDF' },
      ],
      broker: {
        name: 'Emma Thompson',
        company: 'AcquizGroup',
        phone: '(604) 555-2345',
        email: 'e.thompson@acquizgroup.com',
        image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=150&h=150&fit=crop',
      },
    },
    fr: {
      title: 'Commerce en Ligne - Produits de Beauté Naturels',
      location: 'Vancouver, BC, Canada',
      badges: [
        { label: 'Seller Financing', type: 'green' },
        { label: '🇨🇦 Canada', type: 'gold' },
      ],
      mainImage: 'https://images.unsplash.com/photo-1596462502278-27bfdc403348?w=1200&h=600&fit=crop',
      thumbnails: [
        'https://images.unsplash.com/photo-1556228720-195a672e8a03?w=200&h=150&fit=crop',
        'https://images.unsplash.com/photo-1612817288484-6f916006741a?w=200&h=150&fit=crop',
        'https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?w=200&h=150&fit=crop',
        'https://images.unsplash.com/photo-1608248543803-ba4f8c70ae0b?w=200&h=150&fit=crop',
      ],
      financials: {
        askingPrice: '$450,000',
        cashFlow: '$125,000',
        grossRevenue: '$850,000',
        sde: '$130,000',
        ebitda: '$120,000',
        realEstate: 'À domicile',
        established: '2019',
        ffe: '$15,000',
        inventory: '$45,000',
      },
      description: [
        "E-commerce établi avec marque forte et base de clients fidèles. Plus de 15,000 commandes annuelles. Présence sur Amazon et boutique Shopify. Inventaire inclus. Marges brutes de 65%. Automatisation avancée.",
        "La marque a développé une forte présence sur les réseaux sociaux avec plus de 50K abonnés sur les différentes plateformes. Les produits proviennent de fournisseurs certifiés avec des formulations exclusives. Taux de rétention client de 40%.",
        "Opportunité idéale pour un entrepreneur cherchant à entrer dans le marché croissant de la beauté naturelle avec une marque établie et un modèle d'affaires éprouvé.",
      ],
      detailedInfo: [
        { label: 'Emplacement', value: 'Vancouver, BC' },
        { label: 'Inventaire', value: '$45,000 inclus' },
        { label: 'Immobilier', value: 'À domicile' },
        { label: 'Commandes Annuelles', value: '15,000+' },
        { label: 'Marge Brute', value: '65%' },
        { label: 'Année Établie', value: '2019' },
        { label: 'Abonnés Sociaux', value: '50K+' },
        { label: 'Plateformes', value: 'Amazon, Shopify' },
        { label: 'Rétention Client', value: '40%' },
        { label: 'Soutien & Formation', value: '3 mois de transition' },
        { label: 'Raison de Vente', value: 'Nouveau projet' },
        { label: "Nombre d'Employés", value: '2 temps partiel' },
      ],
      financingOptions: [
        {
          title: 'Financement Vendeur',
          details: "Jusqu'à 40% avec acheteur approuvé",
          lenderType: 'Prêteur Privé',
          loanProductType: 'Note du Vendeur',
          loanAmountOffered: 100000,
          loanTermLengthMonths: 36,
          interestRateType: 'fixed',
          interestRateValue: 5.0,
          amortizationType: 'linear',
          collateralRequired: true,
          collateralRequirements: ['All Business Assets (First Lien)', 'Inventory'],
          personalGuaranteeRequired: true,
          creditScoreRequired: true,
          creditScoreMinimum: 650,
          estimatedClosingTimelineDays: 21,
          offerExpirationDate: '2026-06-30',
        },
        {
          title: 'Prêteur E-commerce',
          details: 'Financement spécialisé e-commerce',
          lenderType: 'Prêteur Spécialisé',
          loanProductType: 'Financement Basé sur les Revenus',
          loanAmountOffered: 175000,
          loanTermLengthMonths: 48,
          interestRateType: 'fixed',
          interestRateValue: 9.0,
          amortizationType: 'linear',
          arrangementFeePercent: 2.5,
          maintenanceCovenantsRequired: false,
          collateralRequired: true,
          collateralRequirements: ['All Business Assets (First Lien)', 'Accounts Receivable', 'Intellectual Property'],
          personalGuaranteeRequired: false,
          creditScoreRequired: true,
          creditScoreMinimum: 680,
          acceptForeignCreditScores: true,
          foreignCreditScoreMinimum: 700,
          acceptedForeignCreditCountries: ['Canada', 'Royaume-Uni', 'Australie'],
          estimatedClosingTimelineDays: 14,
          offerExpirationDate: '2026-07-15',
        },
        {
          title: "Financement d'Inventaire",
          details: "Financement d'inventaire séparé disponible",
          lenderType: 'Prêteur Spécialisé',
          loanProductType: 'ABL - Ligne Inventaire',
          loanAmountOffered: 80000,
          loanTermLengthMonths: 12,
          interestRateType: 'floating',
          interestRateValue: 8.5,
          interestRateMarginBps: 300,
          amortizationType: 'linear',
          isABLOffer: true,
          ABL_Inventory_EligiblityPercent: 60,
          ABL_Cash_Sweep: true,
          drawFeePercentABL: 0.25,
          collateralRequired: true,
          collateralRequirements: ['Inventory'],
          personalGuaranteeRequired: false,
          creditScoreRequired: true,
          creditScoreMinimum: 660,
          estimatedClosingTimelineDays: 10,
          offerExpirationDate: '2026-06-15',
        },
      ],
      documents: [
        { name: "Mémorandum d'Information Confidentiel (CIM)", type: 'PDF' },
        { name: 'États Financiers (3 ans)', type: 'PDF' },
        { name: 'Analytique Amazon & Shopify', type: 'PDF' },
        { name: "Rapport d'Évaluation d'Inventaire", type: 'PDF' },
        { name: 'Accords Fournisseurs', type: 'PDF' },
      ],
      broker: {
        name: 'Emma Thompson',
        company: 'AcquizGroup',
        phone: '(604) 555-2345',
        email: 'e.thompson@acquizgroup.com',
        image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=150&h=150&fit=crop',
      },
    },
  },
  // listing-006: Automotive Manufacturing
  {
    id: 'listing-006',
    slug: 'automotive-manufacturer',
    en: {
      title: 'Specialized Automotive Parts Manufacturer',
      location: 'Detroit, MI, United States',
      badges: [
        { label: '🇺🇸 USA', type: 'gold' },
        { label: '✓ E2 Visa Eligible', type: 'blue' },
      ],
      mainImage: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=1200&h=600&fit=crop',
      thumbnails: [
        'https://images.unsplash.com/photo-1565043666747-69f6646db940?w=200&h=150&fit=crop',
        'https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?w=200&h=150&fit=crop',
        'https://images.unsplash.com/photo-1513828583688-c52646db42e6?w=200&h=150&fit=crop',
        'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=200&h=150&fit=crop',
      ],
      financials: {
        askingPrice: '$3,200,000',
        cashFlow: '$750,000',
        grossRevenue: '$5,200,000',
        sde: '$785,000',
        ebitda: '$770,000',
        realEstate: 'Owned (45,000 sq ft)',
        established: '1994',
        ffe: '$1,200,000',
        inventory: '$380,000',
      },
      description: [
        "Established manufacturer with 30 years of experience. Long-term contracts with major automotive manufacturers. 45,000 sq ft facilities with modern CNC equipment. ISO 9001 and IATF 16949 certifications.",
        'The company has built strong relationships with tier-1 automotive suppliers and OEMs. The experienced workforce and proven quality systems ensure consistent delivery and customer satisfaction.',
        'Excellent opportunity for strategic acquirer or private equity looking to enter the automotive supply chain with an established operation and long-term customer relationships.',
      ],
      detailedInfo: [
        { label: 'Location', value: 'Detroit, MI' },
        { label: 'Inventory', value: '$380,000' },
        { label: 'Real Estate', value: 'Owned (45,000 sq ft)' },
        { label: 'Certifications', value: 'ISO 9001, IATF 16949' },
        { label: 'Customer Base', value: 'Major OEMs & Tier-1' },
        { label: 'Year Established', value: '1994' },
        { label: 'Equipment (FF&E)', value: '$1,200,000' },
        { label: 'Technology', value: 'Modern CNC equipment' },
        { label: 'Contract Backlog', value: '24+ months' },
        { label: 'Support & Training', value: '12 months transition' },
        { label: 'Reason for Sale', value: 'Retirement' },
        { label: 'Number of Employees', value: '45' },
      ],
      financingOptions: [
        {
          title: 'SBA 7(a) Loan',
          details: 'Pre-qualified for SBA financing',
          lenderType: 'Commercial Bank',
          loanProductType: 'SBA 7(a)',
          loanAmountOffered: 2000000,
          loanTermLengthMonths: 120,
          interestRateType: 'floating',
          interestRateValue: 7.25,
          interestRateMarginBps: 275,
          interestRateFloor: 5.0,
          amortizationType: 'linear',
          arrangementFeePercent: 2.0,
          maintenanceCovenantsRequired: true,
          netDebtToEBITDAMaxAllowed: 3.5,
          dscrMinimumRequired: 1.25,
          dscrCalculationBase: 'ebitda',
          dividendRestrictions: 'Max 40% of net income',
          collateralRequired: true,
          collateralRequirements: ['All Business Assets (First Lien)', 'Equipment & Machinery', 'Business Real Estate (First Lien)'],
          personalGuaranteeRequired: true,
          realEstateConditioned: true,
          creditScoreRequired: true,
          creditScoreMinimum: 700,
          acceptForeignCreditScores: true,
          foreignCreditScoreMinimum: 720,
          acceptedForeignCreditCountries: ['Canada'],
          acceptForeignCollateral: false,
          estimatedClosingTimelineDays: 75,
          offerExpirationDate: '2026-08-01',
        },
        {
          title: 'Asset-Based Lending',
          details: 'Equipment and real estate as collateral',
          lenderType: 'Specialty Lender',
          loanProductType: 'ABL Revolver',
          loanAmountOffered: 1500000,
          loanTermLengthMonths: 36,
          interestRateType: 'floating',
          interestRateValue: 8.0,
          interestRateMarginBps: 350,
          amortizationType: 'linear',
          isABLOffer: true,
          ABL_AR_EligiblityPercent: 85,
          ABL_Inventory_EligiblityPercent: 50,
          ABL_Cash_Sweep: true,
          drawFeePercentABL: 0.25,
          arrangementFeePercent: 1.5,
          exitFeePercent: 1.0,
          maintenanceCovenantsRequired: true,
          dscrMinimumRequired: 1.1,
          dscrCalculationBase: 'cfads',
          collateralRequired: true,
          collateralRequirements: ['All Business Assets (First Lien)', 'Accounts Receivable', 'Inventory', 'Equipment & Machinery'],
          personalGuaranteeRequired: true,
          creditScoreRequired: true,
          creditScoreMinimum: 680,
          estimatedClosingTimelineDays: 30,
          offerExpirationDate: '2026-07-15',
        },
        {
          title: 'Earnout Structure',
          details: 'Performance-based payment available',
          lenderType: 'Private Lender',
          loanProductType: 'Mezzanine / Earnout',
          loanAmountOffered: 750000,
          loanTermLengthMonths: 48,
          interestRateType: 'fixed',
          interestRateValue: 12.0,
          amortizationType: 'in_fine',
          isSubordinatedDebt: true,
          subordinationToSeniorDebt: true,
          interestGracePeriodMonths: 6,
          balloonPaymentYear: 4,
          balloonPaymentAmount: 750000,
          maintenanceCovenantsRequired: false,
          collateralRequired: false,
          personalGuaranteeRequired: false,
          creditScoreRequired: false,
          estimatedClosingTimelineDays: 21,
          offerExpirationDate: '2026-06-30',
        },
      ],
      documents: [
        { name: 'Confidential Information Memorandum (CIM)', type: 'PDF' },
        { name: 'Financial Statements (5 years)', type: 'PDF' },
        { name: 'Customer Contract Summary', type: 'PDF' },
        { name: 'Equipment List & Appraisal', type: 'PDF' },
        { name: 'Real Estate Appraisal', type: 'PDF' },
        { name: 'Quality Certifications', type: 'PDF' },
      ],
      broker: {
        name: 'David Anderson',
        company: 'AcquizGroup',
        phone: '(313) 555-6789',
        email: 'd.anderson@acquizgroup.com',
        image: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=150&h=150&fit=crop',
      },
    },
    fr: {
      title: 'Fabricant de Pièces Automobiles Spécialisées',
      location: 'Detroit, MI, États-Unis',
      badges: [
        { label: '🇺🇸 USA', type: 'gold' },
        { label: '✓ Éligible Visa E2', type: 'blue' },
      ],
      mainImage: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=1200&h=600&fit=crop',
      thumbnails: [
        'https://images.unsplash.com/photo-1565043666747-69f6646db940?w=200&h=150&fit=crop',
        'https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?w=200&h=150&fit=crop',
        'https://images.unsplash.com/photo-1513828583688-c52646db42e6?w=200&h=150&fit=crop',
        'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=200&h=150&fit=crop',
      ],
      financials: {
        askingPrice: '$3,200,000',
        cashFlow: '$750,000',
        grossRevenue: '$5,200,000',
        sde: '$785,000',
        ebitda: '$770,000',
        realEstate: 'Propriété (45,000 pi²)',
        established: '1994',
        ffe: '$1,200,000',
        inventory: '$380,000',
      },
      description: [
        "Fabricant établi avec 30 ans d'expérience. Contrats long-terme avec constructeurs automobiles majeurs. Installations de 45,000 pi² avec équipement CNC moderne. Certifications ISO 9001 et IATF 16949.",
        "L'entreprise a développé de solides relations avec les fournisseurs automobiles de niveau 1 et les équipementiers d'origine. La main-d'œuvre expérimentée et les systèmes de qualité éprouvés assurent une livraison cohérente et la satisfaction des clients.",
        "Excellente opportunité pour un acquéreur stratégique ou un fonds de private equity cherchant à entrer dans la chaîne d'approvisionnement automobile avec une opération établie et des relations clients à long terme.",
      ],
      detailedInfo: [
        { label: 'Emplacement', value: 'Detroit, MI' },
        { label: 'Inventaire', value: '$380,000' },
        { label: 'Immobilier', value: 'Propriété (45,000 pi²)' },
        { label: 'Certifications', value: 'ISO 9001, IATF 16949' },
        { label: 'Base de Clients', value: 'OEMs majeurs & Niveau 1' },
        { label: 'Année Établie', value: '1994' },
        { label: 'Équipement (FF&E)', value: '$1,200,000' },
        { label: 'Technologie', value: 'Équipement CNC moderne' },
        { label: 'Carnet de Commandes', value: '24+ mois' },
        { label: 'Soutien & Formation', value: '12 mois de transition' },
        { label: 'Raison de Vente', value: 'Retraite' },
        { label: "Nombre d'Employés", value: '45' },
      ],
      financingOptions: [
        {
          title: 'Prêt SBA 7(a)',
          details: 'Pré-qualifié pour financement SBA',
          lenderType: 'Banque Commerciale',
          loanProductType: 'SBA 7(a)',
          loanAmountOffered: 2000000,
          loanTermLengthMonths: 120,
          interestRateType: 'floating',
          interestRateValue: 7.25,
          interestRateMarginBps: 275,
          interestRateFloor: 5.0,
          amortizationType: 'linear',
          arrangementFeePercent: 2.0,
          maintenanceCovenantsRequired: true,
          netDebtToEBITDAMaxAllowed: 3.5,
          dscrMinimumRequired: 1.25,
          dscrCalculationBase: 'ebitda',
          dividendRestrictions: 'Max 40% du revenu net',
          collateralRequired: true,
          collateralRequirements: ['All Business Assets (First Lien)', 'Equipment & Machinery', 'Business Real Estate (First Lien)'],
          personalGuaranteeRequired: true,
          realEstateConditioned: true,
          creditScoreRequired: true,
          creditScoreMinimum: 700,
          acceptForeignCreditScores: true,
          foreignCreditScoreMinimum: 720,
          acceptedForeignCreditCountries: ['Canada'],
          acceptForeignCollateral: false,
          estimatedClosingTimelineDays: 75,
          offerExpirationDate: '2026-08-01',
        },
        {
          title: 'Prêt Basé sur Actifs',
          details: 'Équipement et immobilier en garantie',
          lenderType: 'Prêteur Spécialisé',
          loanProductType: 'ABL Revolver',
          loanAmountOffered: 1500000,
          loanTermLengthMonths: 36,
          interestRateType: 'floating',
          interestRateValue: 8.0,
          interestRateMarginBps: 350,
          amortizationType: 'linear',
          isABLOffer: true,
          ABL_AR_EligiblityPercent: 85,
          ABL_Inventory_EligiblityPercent: 50,
          ABL_Cash_Sweep: true,
          drawFeePercentABL: 0.25,
          arrangementFeePercent: 1.5,
          exitFeePercent: 1.0,
          maintenanceCovenantsRequired: true,
          dscrMinimumRequired: 1.1,
          dscrCalculationBase: 'cfads',
          collateralRequired: true,
          collateralRequirements: ['All Business Assets (First Lien)', 'Accounts Receivable', 'Inventory', 'Equipment & Machinery'],
          personalGuaranteeRequired: true,
          creditScoreRequired: true,
          creditScoreMinimum: 680,
          estimatedClosingTimelineDays: 30,
          offerExpirationDate: '2026-07-15',
        },
        {
          title: 'Structure Earnout',
          details: 'Paiement basé sur la performance disponible',
          lenderType: 'Prêteur Privé',
          loanProductType: 'Mezzanine / Earnout',
          loanAmountOffered: 750000,
          loanTermLengthMonths: 48,
          interestRateType: 'fixed',
          interestRateValue: 12.0,
          amortizationType: 'in_fine',
          isSubordinatedDebt: true,
          subordinationToSeniorDebt: true,
          interestGracePeriodMonths: 6,
          balloonPaymentYear: 4,
          balloonPaymentAmount: 750000,
          maintenanceCovenantsRequired: false,
          collateralRequired: false,
          personalGuaranteeRequired: false,
          creditScoreRequired: false,
          estimatedClosingTimelineDays: 21,
          offerExpirationDate: '2026-06-30',
        },
      ],
      documents: [
        { name: "Mémorandum d'Information Confidentiel (CIM)", type: 'PDF' },
        { name: 'États Financiers (5 ans)', type: 'PDF' },
        { name: 'Résumé des Contrats Clients', type: 'PDF' },
        { name: "Liste d'Équipement & Évaluation", type: 'PDF' },
        { name: 'Évaluation Immobilière', type: 'PDF' },
        { name: 'Certifications Qualité', type: 'PDF' },
      ],
      broker: {
        name: 'David Anderson',
        company: 'AcquizGroup',
        phone: '(313) 555-6789',
        email: 'd.anderson@acquizgroup.com',
        image: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=150&h=150&fit=crop',
      },
    },
  },
];

export const getListingBySlug = (slug: string): ListingDetailData | undefined => {
  return LISTING_DETAILS.find((listing) => listing.slug === slug);
};

export const getListingById = (id: string): ListingDetailData | undefined => {
  return LISTING_DETAILS.find((listing) => listing.id === id);
};
